"use client";
"use no memo";

import { useQuery } from "@apollo/client/react";
import type {
  ColumnDef,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import Link from "next/link";
import { useMemo, useState } from "react";
import { LuBadgeDollarSign, LuUserPlus as LuUserPlus2 } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import TablePagination from "@/components/shared/table-pagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import type { TokenBalance } from "@/graphql/codegen/graphql";
import {
  TokenBalancesOrderBy,
  TokensBalancesDocument,
} from "@/graphql/codegen/graphql";
import {
  APP_NAME_GENERIC,
  BONDING_CURVE_DISPLAY_NAME,
  BONDING_CURVE_SHORTHAND_DISPLAY_NAME,
  DESO_APP_NAME,
  DESO_TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { pluralize } from "@/utils/helpers";
import { getOrderByKey } from "@/utils/trade";

import TrendingCoinsTable from "../trending-coins/trending-coins-table";
import { tableColumns } from "./creator-coins-v1-table-columns";

const COLUMNS_WIDTH = [3, 43, 16, 16, 16, 6];
const DEFAULT_ITEMS_PER_PAGE = 10;

enum TabsValue {
  TokensPurchased = "tokensPurchased",
  TokensReceived = "tokensReceived",
}

const TabItems = [
  { value: TabsValue.TokensPurchased, label: "Tokens Purchased" },
  { value: TabsValue.TokensReceived, label: "Tokens Received" },
];

const CreatorCoinV1TableWrapperContent = ({
  publicKey,
}: {
  publicKey: string;
}) => {
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentTab, setCurrentTab] = useState<TabsValue>(
    TabsValue.TokensPurchased,
  );
  const [columnSorting, setColumnSorting] = useState<SortingState>([
    { id: "balanceNanos", desc: true },
  ]);

  const { loading: loadingExchangeRates, appState } = useFocusMarketData();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });

  const queryVariables = useMemo(
    () => ({
      first: itemsPerPage,
      offset: pageOffset,
      orderBy: getOrderByKey(
        columnSorting,
        TokenBalancesOrderBy,
        TokenBalancesOrderBy.BalanceNanosDesc,
      ),
      filter: {
        hasPurchased: {
          equalTo: currentTab !== TabsValue.TokensReceived,
        },
        isDaoCoin: {
          equalTo: false,
        },
        creator: {
          publicKey: {
            notEqualTo: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
          },
        },
        holder: {
          publicKey: {
            equalTo: publicKey,
          },
        },
      },
    }),
    [columnSorting, currentTab, itemsPerPage, pageOffset, publicKey],
  );

  const {
    data: tokenBalancesData,
    loading: loadingTokensBalances,
    refetch: refetchTokensBalances,
  } = useQuery(TokensBalancesDocument, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
  });

  const visibleColumns = useMemo(
    () =>
      tableColumns({
        appState,
      }),
    [appState],
  );

  const tokensBalances =
    (tokenBalancesData?.tokenBalances?.nodes?.filter(
      Boolean,
    ) as TokenBalance[]) ?? [];
  const totalCount = tokenBalancesData?.tokenBalances?.totalCount ?? 0;
  const firstLoading = loadingTokensBalances && !tokenBalancesData;

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: itemsPerPage,
    });
    setPageOffset(0);
  };

  const resetTableState = () => {
    resetPagination();
    setColumnSorting([{ id: "balanceNanos", desc: true }]);
  };

  const onPaginate = (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });
    setPageOffset(offset);
  };

  const handleTabChange = (value: string) => {
    setCurrentTab(value as TabsValue);
    resetTableState();
  };

  const handleColumnSortingChange = (sorting: SortingState) => {
    setColumnSorting(sorting);
    resetPagination();
  };

  const refetchCurrentTable = async () => {
    await refetchTokensBalances();
  };

  const renderTabContent = (
    <>
      <TrendingCoinsTable
        wrapperClassName="min-w-[600px] md:min-w-auto"
        loading={loadingTokensBalances}
        columns={visibleColumns as Array<ColumnDef<TokenBalance>>}
        data={tokensBalances}
        columnSorting={columnSorting}
        setColumnSorting={(sorting) =>
          handleColumnSortingChange(sorting as SortingState)
        }
        pagination={pagination}
        pageOffset={pageOffset}
        columnsWidth={COLUMNS_WIDTH}
        refetchData={refetchCurrentTable}
      />
      <TablePagination
        loading={loadingTokensBalances}
        pagination={pagination}
        onPaginate={onPaginate}
        itemsPerPage={itemsPerPage}
        totalCount={totalCount}
      />
      <div className="mt-2 mb-4 text-center text-xs text-muted">
        Total: {pluralize(totalCount, `${BONDING_CURVE_DISPLAY_NAME} Token`)}
      </div>
    </>
  );

  if (
    !firstLoading &&
    currentTab === TabsValue.TokensPurchased &&
    totalCount === 0
  ) {
    return null;
  }

  return (
    <>
      <div
        className="
          mt-6 mb-2 flex flex-col items-start gap-4 border-t border-border-light
          pt-6
          sm:flex-row sm:items-center sm:justify-between sm:gap-0
        "
      >
        <h3 className="flex items-center font-medium text-muted">
          <LuBadgeDollarSign className="mr-2 text-xl" />
          <span className="mr-1">{BONDING_CURVE_DISPLAY_NAME} Tokens</span>
          <InfoTooltip
            iconSize={16}
            className="max-w-[300px] min-w-[300px]"
            text={
              <>
                <p className="mb-4" key="row-first">
                  {BONDING_CURVE_DISPLAY_NAME} Tokens, or &quot;
                  {BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Tokens&quot; for short,
                  have a simpler implementation than {DESO_TOKEN_DISPLAY_NAME}{" "}
                  Tokens. {BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Tokens support
                  market buys and sells against a fixed bonding curve, rather
                  than supporting a full order book like{" "}
                  {DESO_TOKEN_DISPLAY_NAME} Tokens do. {APP_NAME_GENERIC} uses
                  the {DESO_TOKEN_DISPLAY_NAME} Token standard for launching new
                  coins, but other apps can choose between the{" "}
                  {DESO_TOKEN_DISPLAY_NAME} Token standard and the{" "}
                  {BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Token standard
                  depending on their needs.
                </p>
                <p key="row-second">
                  Ownership of profiles that have a &quot;Tweet to Claim&quot;
                  associated with them from existing {DESO_APP_NAME} apps will
                  still be optionally transferred to creators who join{" "}
                  {APP_NAME_GENERIC} as long as they post the standard{" "}
                  {APP_NAME_GENERIC} Tweet to X and leave it up for 24h.
                  However, note that taking ownership of one's{" "}
                  {BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Token is optional.
                </p>
              </>
            }
          />
        </h3>
        <Link
          href={`${process.env.NEXT_PUBLIC_DIAMOND_APP_URI}/creators`}
          target="_blank"
        >
          <Button variant="default" size="sm">
            <span className="flex items-center gap-1">
              <LuUserPlus2 className="size-4" />
              Buy {BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Tokens
            </span>
          </Button>
        </Link>
      </div>
      <div className="mb-4 w-full text-muted">
        {firstLoading || loadingExchangeRates ? (
          <Skeleton className="mt-4 h-[320px] w-full" key="tokens-key" />
        ) : (
          <Tabs value={currentTab} onValueChange={handleTabChange}>
            <TabsList className="mb-3 border-none">
              {TabItems.map((tab) => (
                <TabsTrigger value={tab.value} key={tab.value}>
                  <span>{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {TabItems.map((tab) => (
              <TabsContent value={tab.value} key={tab.value}>
                {renderTabContent}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </>
  );
};

const CreatorCoinV1TableWrapper = ({ publicKey }: { publicKey: string }) => {
  return (
    <CreatorCoinV1TableWrapperContent key={publicKey} publicKey={publicKey} />
  );
};

export default CreatorCoinV1TableWrapper;
